import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ArrowBackOutlined, FolderOpen, MoreHoriz } from '@material-ui/icons';
import { useParams } from 'react-router-dom';

// Components
import { useForm, TextField } from '../../components/FormInputs';
import CustomMenu from '../../components/menu';
import {
  MaterialAutocompleteStyles,
  StyledPaper,
  StyledUser,
} from '../../components/FormInputs/autoComplete';
import { Fab, Normal } from '../../components/Button';
import AssetOverview from './AssetOverview';
import { getImportAssetStatusService } from '../duck/operations';
import { enqueueSnackbar } from '../../snackbar/duck/actions';
import { selectItemsAction, setImportAssetStatus } from '../duck/actions';
import serviceCall from '../../../utils/api';
import apiConfig from '../../../config';
import { SELECTION_KEYS } from '../constants';
import { required } from '../../components/FormInputs/helperFunctions';
import { formatLabelValue } from '../../helperFunctions';
import { MONTH_NAMES } from '../../constants';
import Tooltip from '../../components/tooltip';

// Styles
import { Body, Footer } from '../../styles/modal';
import {
  Text,
  IconBreadCrumbStyles,
  BreadCrumbTextStyles,
} from '../../styles/common';
import { colors, font } from '../../styles/variables';
import { whiteAlpha } from '../../styles/utils';

const MenuStyledPaper = styled(StyledPaper)`
  &.MuiPaper-root {
    max-height: 15rem;
    overflow-y: auto;
  }
`;

const FoldersParentListStyles = styled.ul`
  list-style: none;
  max-height: 20rem;
  overflow-y: auto;

  > li {
    align-items: start;
    button {
      background: transparent;
      text-align: left;
      cursor: pointer;
      border: none;
      padding: 0.5rem 0;
      color: ${colors?.white};
      font-weight: ${font.normal};
      &:hover {
        color: ${colors?.blue?.primary};
      }
    }
  }
`;

const BreadcrumbStyles = styled(IconBreadCrumbStyles)`
  margin-bottom: 1.5rem;

  .project-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    p {
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .options {
    .MuiSvgIcon-root {
      margin-left: 0.5rem;
      width: 2.2rem;
      height: 2.2rem;
      fill: ${colors.white};
    }
  }

  .MuiFab-root {
    width: 2.5rem;
    height: 2.5rem;

    .MuiSvgIcon-root {
      font-size: 1.3rem;
    }
  }

  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    text-align: left;
    padding: 0;
    margin-left: 0.5rem;
  }
`;

const INITIAL_STATE = {
  selectedBucket: {
    value: '',
    error: undefined,
    required: true,
    validation: required,
  },

  selectedFolder: {
    value: '',
    error: undefined,
  },
};

const date = new Date();
const UTC_DATE = `${date.getUTCDate()}-${
  MONTH_NAMES[date.getUTCMonth()]
}-${date.getUTCFullYear()}`;

const ShareToAmazonS3 = ({ toggleModal, assetDetails }) => {
  const dispatch = useDispatch();
  const { project_db_id } = useParams();
  const selectedItems = useSelector(store => store?.storyboard?.selectedItems);
  const [projectInputValue, setProjectInputValue] = useState('');
  const [sourceBucket, setSourceBucket] = useState('');
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [bucketOptions, setBucketOptions] = useState([]);
  const [folderOptions, setFolderOptions] = useState([]);
  const [isSubmitting, setSubmitting] = useState(false);

  const onModalClose = () => toggleModal(null, false, null);

  const getImportStatus = async () => {
    const res = await getImportAssetStatusService({
      project_db_id,
      entries_per_page: 25,
      page_no: 1,
    });

    if (res?.data?.response) {
      if (res?.data?.data?.show_notification) {
        dispatch(setImportAssetStatus({ data: res?.data?.data }));
      }
    }
  };

  const { formValues, onSubmit, updateValues } = useForm(
    INITIAL_STATE,
    async () => {
      setSubmitting(true);

      const {
        data: { response, errormessage, data },
      } = await serviceCall.post(`${apiConfig.upload}/s3-uploads/`, {
        files: [],
        folders: [],
        ingest_db_ids: assetDetails?.isMultiple
          ? Object.keys(selectedItems)
          : [assetDetails?.ingest_db_id],
        project_db_id,
        source_bucket: sourceBucket,
        destination_bucket: formValues.selectedBucket.value?.output_bucket,
        destination_folder:
          formValues.selectedFolder.value?.value ||
          formValues.selectedBucket.value?.output_folder_path,
        integration_db_id: formValues.selectedBucket.value?.integration_db_id,
      });

      setSubmitting(false);
      if (response) {
        if (assetDetails?.isMultiple) {
          dispatch(
            selectItemsAction({
              type: SELECTION_KEYS.reset,
            })
          );
        }
        onModalClose();
        dispatch(
          enqueueSnackbar({
            message: {
              messageHead: 'Success',
              messageBody: data?.message,
              variant: 'success',
            },
          })
        );
        setTimeout(getImportStatus, 1000);
      } else {
        dispatch(
          enqueueSnackbar({
            message: {
              messageHead: 'Error',
              messageBody: errormessage,
              variant: 'error',
            },
          })
        );
      }
    }
  );

  const fetchBuckets = async () => {
    try {
      const {
        data: { response, data, errormessage },
      } = await serviceCall.get(`${apiConfig.upload}/s3-buckets`, {
        project_db_id,
        entries_per_page: 100,
        page_no: 1,
      });
      if (response) {
        const options = data?.integrations?.reduce((acc, curr) => {
          acc.push({
            ...curr,
            label: curr.output_bucket,
            value: curr.output_folder_path,
          });
          return acc;
        }, []);
        setSourceBucket(data?.source_bucket);
        setBucketOptions(options);
      } else {
        throw new Error(errormessage);
      }
    } catch (err) {
      console.error('🚀 ~ fetchBuckets ~ err:', err);
    }
  };

  const fetchFolders = useCallback(
    async (folder, bucket) => {
      const { value, label } = formValues?.selectedBucket?.value;
      const request = {
        project_db_id,
        bucket: bucket || label,
        entries_per_page: 500,
        page_no: 1,
        folder_path: folder || value,
      };

      try {
        const {
          data: { response, data, errormessage },
        } = await serviceCall.get(`${apiConfig.upload}/folders/`, request);
        if (response) {
          const options = formatLabelValue(data?.folders, 'label', 'value');
          setFolderOptions(options);
        } else {
          throw new Error(errormessage);
        }
      } catch (err) {
        console.error('🚀 ~ fetchFolders ~ err:', err?.message);
      }
    },
    [formValues?.selectedBucket?.value]
  );

  const onBucketChange = (e, option, reason) => {
    if (reason === 'select-option') {
      setFolderOptions([]); // reset
      setSelectedFolders([]); // reset
      fetchFolders(option?.value, option?.label);
      updateValues({
        ...formValues,
        selectedBucket: {
          ...formValues.selectedBucket,
          value: option,
          error: undefined,
        },
        selectedFolder: {
          ...formValues.selectedFolder,
          value: '',
        },
      });
    }
  };

  const onBreadcrumbProject = () => {
    fetchFolders();
    setSelectedFolders([]);
    updateValues({
      ...formValues,
      selectedFolder: {
        ...formValues.selectedFolder,
        value: '',
      },
    });
  };

  const onFolderSelect = useCallback(
    (option, add) => {
      const updatedOption = {
        label: option?.name || option?.label || '',
        value: option?.value || option?.field || '',
      };

      updateValues({
        ...formValues,
        selectedFolder: {
          ...formValues.selectedFolder,
          value: updatedOption,
          error: undefined,
        },
      });

      fetchFolders(option?.value || option?.field);

      if (!add) {
        if (option?.index?.toString()) {
          const updated = option.index - 1;
          if (updated || updated.toString() === '0') {
            setSelectedFolders(selectedFolders.slice(0, option.index));
          } else {
            setSelectedFolders(selectedFolders.slice(0, -1));
          }
        } else {
          setSelectedFolders(selectedFolders.slice(0, -1));
        }
      } else {
        setSelectedFolders(prev => [...prev, option]);
      }
    },
    [selectedFolders, formValues?.selectedBucket?.value]
  );

  useEffect(() => {
    fetchBuckets();
  }, []);

  const onBack = useCallback(() => {
    const copy = [...selectedFolders];
    const lastTwoElement = copy.slice(-2);
    onFolderSelect(
      lastTwoElement?.length === 2 ? lastTwoElement[0] : undefined
    );
  }, [selectedFolders]);

  const formatedSelectedFolders = useMemo(() => {
    const copy = [...selectedFolders];
    return copy.slice(0, -1).map((el, index) => ({
      index: index + 1,
      name: el.label,
      field: el.value,
      isSelected: false,
      icon: <FolderOpen style={{ marginRight: '0.5rem' }} />,
    }));
  }, [selectedFolders]);

  return (
    <form onSubmit={onSubmit} name='share-to-contido'>
      <Body>
        {(formValues?.selectedFolder?.value?.value ||
          selectedFolders?.length > 0) && (
          <BreadcrumbStyles>
            <Fab
              type='button'
              onClick={onBack}
              size='small'
              color='primary'
              aria-label='back'
              icon={<ArrowBackOutlined />}
            />

            <Tooltip
              title={formValues?.selectedBucket?.value?.label}
              hide
              placement='top'
              arrow>
              <button
                type='button'
                className='project-btn'
                onClick={onBreadcrumbProject}>
                <span>
                  <BreadCrumbTextStyles className='first'>
                    {formValues?.selectedBucket?.value?.label}
                  </BreadCrumbTextStyles>
                </span>
              </button>
            </Tooltip>

            {selectedFolders?.length > 1 && (
              <>
                <Text>/</Text>
                <div className='options'>
                  <CustomMenu
                    maxHeight='20rem'
                    menuItemTextTransform={false}
                    onMenuItemClick={item => onFolderSelect(item)}
                    menuList={formatedSelectedFolders}
                    icon={<MoreHoriz />}
                  />
                </div>
              </>
            )}
            <Text>/</Text>
            <Tooltip
              title={selectedFolders[selectedFolders?.length - 1]?.label}
              hide
              placement='top'
              arrow>
              <BreadCrumbTextStyles className='last'>
                {selectedFolders[selectedFolders?.length - 1]?.label}
              </BreadCrumbTextStyles>
            </Tooltip>
          </BreadcrumbStyles>
        )}

        {!assetDetails?.isMultiple && (
          <div className='pb-1-5'>
            {assetDetails?.shared_detail?.s3_shared_by?.name && (
              <Text padding='0 0 1.5rem 0' color={whiteAlpha(0.6)}>
                This asset has been shared by{' '}
                {assetDetails?.shared_detail?.s3_shared_by?.name}
              </Text>
            )}
            <AssetOverview {...assetDetails} />
          </div>
        )}

        <MaterialAutocompleteStyles
          id='selectedBucket'
          disableClearable
          autoHighlight
          getOptionLabel={option => `${option.label}/${option.value}` ?? ''}
          onChange={onBucketChange}
          options={bucketOptions}
          PaperComponent={paperProps => <MenuStyledPaper {...paperProps} />}
          getOptionSelected={(option, value) => option.value === value.value}
          inputValue={projectInputValue}
          onInputChange={(event, newInputValue) => {
            setProjectInputValue(newInputValue);
          }}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              label='Select Bucket'
              error={!!formValues.selectedBucket.error}
              helperText={formValues.selectedBucket.error}
              value={formValues.selectedBucket?.value?.value ?? ''}
            />
          )}
          renderOption={option => {
            return (
              <StyledUser>
                <Text color={colors.black.default} padding='0'>
                  {option?.label}/{option?.value}
                </Text>
              </StyledUser>
            );
          }}
        />

        {formValues?.selectedBucket?.value?.create_date_folders && (
          <Text padding='0'>
            <span>This asset will be exported to</span> <span>{UTC_DATE}</span>{' '}
            <span>folder in this bucket.</span>
          </Text>
        )}

        <div aria-label='folders'>
          {folderOptions?.length > 0 && (
            <>
              <Text padding='1rem 0' color={whiteAlpha(0.7)}>
                Select Folder
              </Text>
              <FoldersParentListStyles>
                {folderOptions?.map(el => (
                  <li className='flex'>
                    <button
                      className='flex items-center w-full'
                      type='button'
                      key={el?.value}
                      value={el?.value}
                      onClick={() => onFolderSelect(el, 'push')}>
                      <span style={{ display: 'inline-flex' }}>
                        <FolderOpen style={{ marginRight: '0.5rem' }} />
                      </span>
                      <span>{el?.label}</span>
                    </button>
                  </li>
                ))}
              </FoldersParentListStyles>
            </>
          )}

          {/* {formValues?.selectedBucket?.value?.value && !folderOptions?.length && (
            <Text padding='1rem 0' color={whiteAlpha(0.7)}>
              No Folders Found
            </Text>
          )} */}
        </div>
      </Body>

      <Footer>
        <Normal
          onClick={onModalClose}
          color='secondary'
          style={{ marginRight: '2rem' }}>
          Cancel
        </Normal>
        <Normal
          type='submit'
          disabled={isSubmitting || !formValues.selectedBucket.value?.value}
          isLoading={isSubmitting}>
          Share
        </Normal>
      </Footer>
    </form>
  );
};

ShareToAmazonS3.propTypes = {
  toggleModal: PropTypes.func,
  assetDetails: PropTypes.instanceOf(Object),
};

export default ShareToAmazonS3;
